import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl text-sm font-medium transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#33CCFF]/60 focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:scale-[1.03] active:scale-[0.97]",
  {
    variants: {
      variant: {
        default:
          "text-white bg-[linear-gradient(135deg,#33CCFF,#1A66CC)] shadow-[0_4px_20px_rgba(38,153,230,0.4)] hover:brightness-110 hover:shadow-[0_6px_28px_rgba(51,204,255,0.55)]",
        destructive:
          "text-white bg-[linear-gradient(135deg,#ff6b6b,#c0392b)] shadow-[0_4px_20px_rgba(192,57,43,0.4)] hover:brightness-110",
        outline:
          "text-white border border-white/30 bg-white/10 backdrop-blur-md hover:bg-white/20 hover:border-[#33CCFF]/60",
        secondary:
          "text-white bg-white/15 backdrop-blur-md border border-white/20 hover:bg-white/25",
        ghost:
          "text-white/90 hover:bg-white/10 hover:text-white",
        link:
          "text-[#33CCFF] underline-offset-4 hover:underline hover:scale-100",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-xl px-3",
        lg: "h-11 rounded-2xl px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
